import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { getCardFrequencyRank } from "#use-card-frequency-rank";

/**
 * The level (`HSK 1` / `TBCL 3` / `Common` / `Tier 2`) and frequency-rank (`#332`) labels for a
 * morpheme, script- and HSK-version-aware via the reader's account settings.
 *
 * Lives here rather than beside the tile's stats section because both the tile and the row list
 * render these labels, and they must not drift between the two view modes.
 */
export function useMorphemeStatsLabels(graphemeCard: MorphemeDetailsTileSlice) {
  return getMorphemeStatsLabels(graphemeCard, useActorUser());
}

export function getMorphemeStatsLabels(
  graphemeCard: MorphemeDetailsTileSlice,
  {
    hanziScript: script,
    hskVersion,
  }: Pick<ReturnType<typeof useActorUser>, "hanziScript" | "hskVersion">,
) {
  const rank = getCardFrequencyRank(graphemeCard, script);
  const rankLabel = rank != null ? `#${rank}` : null;
  let levelLabel: string | null = null;
  if (script === "traditional" && graphemeCard.morphemeType === "grapheme") {
    const tbclLevel = graphemeCard.tbclLevel ?? null;
    if (tbclLevel !== null) {
      levelLabel = `TBCL ${tbclLevel}`;
    } else {
      switch (graphemeCard.taiwanUsageClassification ?? null) {
        case "common": {
          levelLabel = "Common";
          break;
        }
        case "less-than-common": {
          levelLabel = "Uncommon";
          break;
        }
        case "rarely-used": {
          levelLabel = "Rare";
          break;
        }
      }
    }
  } else {
    let hskLevel: number | null = null;
    switch (hskVersion) {
      case "hsk2": {
        hskLevel = graphemeCard.hsk2010Level ?? null;
        break;
      }
      case "hsk3": {
        hskLevel = graphemeCard.hsk2021Level ?? null;
        break;
      }
      case "hsk2026": {
        hskLevel = graphemeCard.hsk2026Level ?? null;
        break;
      }
    }
    if (hskLevel !== null) {
      levelLabel = `HSK ${hskLevel}`;
    } else if (graphemeCard.tier !== null) {
      levelLabel = `Tier ${graphemeCard.tier}`;
    }
  }
  return { levelLabel, rankLabel };
}
